/* ============================================================
   S3 Sports Supplement Solutions — theme.js v1.0.0
   Shopify AJAX Cart · Cart Drawer · Navigation · Interactions
   ============================================================ */
'use strict';

/* ── Helpers ───────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

function formatMoney(cents) {
  if (window.S3 && window.S3.moneyFormat) {
    return window.S3.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.floor(cents / 100));
  }
  return '$' + (cents / 100).toFixed(2);
}

/* ── Shopify AJAX Cart API ─────────────────────────────────── */
const ShopifyCart = {
  _threshold: (window.S3 && window.S3.freeShipping) ? window.S3.freeShipping : 7500,

  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },

  async add(variantId, qty = 1, properties = {}) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: qty, properties })
    });
    if (!r.ok) {
      const err = await r.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return r.json();
  },

  async update(key, qty) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    return r.json();
  },

  async clear() {
    const r = await fetch('/cart/clear.js', { method: 'POST' });
    return r.json();
  }
};

/* ── Cart Drawer ───────────────────────────────────────────── */
const CartDrawer = {
  drawer:  null,
  overlay: null,
  body:    null,
  subtotal: null,
  progFill: null,
  progText: null,
  counts:  [],

  open() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => $('[aria-label="Close"]', this.drawer)?.focus(), 60);
    this.refresh();
  },

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  async refresh() {
    try {
      const cart = await ShopifyCart.get();
      this._render(cart);
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  },

  _render(cart) {
    this._updateCounts(cart.item_count);
    this._updateProgress(cart.total_price);

    if (!this.body) return;

    if (!cart.items.length) {
      this.body.innerHTML = `
        <div class="cart-empty">
          <svg class="cart-empty__icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272"/>
          </svg>
          <p class="cart-empty__title">Your Cart is Empty</p>
          <p class="cart-empty__sub">Add some elite supplements to get started.</p>
          <a href="/collections/all" class="btn btn--primary btn--sm" onclick="CartDrawer.close()">Shop Now</a>
        </div>`;
      if (this.subtotal) this.subtotal.textContent = formatMoney(0);
      return;
    }

    this.body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__img">
          ${item.image
            ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="72" height="72" style="width:72px;height:72px;object-fit:cover">`
            : `<div class="product-ph" style="height:72px"><span class="product-ph__label">${item.product_title.split(' ')[0]}</span></div>`}
        </div>
        <div class="cart-item__info">
          <p class="cart-item__name">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="qty-stepper">
            <button class="qty-stepper__btn" data-action="dec" aria-label="Decrease">−</button>
            <span class="qty-stepper__val" aria-live="polite">${item.quantity}</span>
            <button class="qty-stepper__btn" data-action="inc" aria-label="Increase">+</button>
          </div>
          <button class="cart-item__remove" data-action="remove">Remove</button>
        </div>
        <div class="cart-item__price">${formatMoney(item.line_price)}</div>
      </div>`).join('');

    if (this.subtotal) this.subtotal.textContent = formatMoney(cart.total_price);
    this._bindItems(cart.items);
  },

  _bindItems(items) {
    $$('.cart-item', this.body).forEach((row, i) => {
      const key = row.dataset.key;
      const item = items.find(it => it.key === key);
      if (!item) return;

      on($('[data-action="inc"]', row), 'click', async () => {
        const c = await ShopifyCart.update(key, item.quantity + 1);
        this._render(c);
      });
      on($('[data-action="dec"]', row), 'click', async () => {
        const c = await ShopifyCart.update(key, item.quantity - 1);
        this._render(c);
      });
      on($('[data-action="remove"]', row), 'click', async () => {
        row.style.cssText = 'opacity:0;transform:translateX(20px);transition:all .2s ease';
        await new Promise(r => setTimeout(r, 220));
        const c = await ShopifyCart.update(key, 0);
        this._render(c);
      });
    });
  },

  _updateCounts(count) {
    this.counts.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    // Also update aria-label on cart buttons
    $$('[data-open-cart]').forEach(btn => {
      btn.setAttribute('aria-label', `Cart, ${count} item${count !== 1 ? 's' : ''}`);
    });
  },

  _updateProgress(totalCents) {
    const threshold = ShopifyCart._threshold;
    const pct = Math.min((totalCents / threshold) * 100, 100);
    const rem = threshold - totalCents;
    if (this.progFill) this.progFill.style.width = pct + '%';
    if (this.progText) {
      this.progText.innerHTML = rem > 0
        ? `Add <strong>${formatMoney(rem)}</strong> more for <strong>FREE shipping</strong>`
        : `🎉 You've unlocked <strong>FREE shipping!</strong>`;
    }
  },

  init() {
    this.drawer   = $('#cart-drawer');
    this.overlay  = $('#cart-overlay');
    this.body     = $('#cart-body');
    this.subtotal = $('#cart-subtotal');
    this.progFill = $('#prog-fill');
    this.progText = $('#prog-text');
    this.counts   = $$('[data-cart-count]');

    $$('[data-open-cart]').forEach(el => on(el, 'click', () => this.open()));
    on($('.cart-drawer__close'), 'click', () => this.close());
    on(this.overlay, 'click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });

    // Listen for custom cart update events
    on(document, 's3:cart-updated', () => this.refresh());

    // Initial count sync from server-rendered HTML (no extra request)
    const badge = $('[data-cart-count]');
    if (badge && badge.textContent.trim() === '0') badge.style.display = 'none';
  }
};

/* ── Add to Cart ───────────────────────────────────────────── */
async function S3AddToCart(formEl, btn) {
  if (!formEl || !btn) return;
  const variantId = formEl.querySelector('[name="id"]')?.value;
  const qty       = parseInt(formEl.querySelector('[name="quantity"]')?.value || 1);
  if (!variantId) return;

  const origText = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span style="opacity:.6">Adding…</span>';

  try {
    await ShopifyCart.add(parseInt(variantId), qty);
    flyToCart(btn);
    S3Toast(btn.closest('[data-product-id]')
      ? btn.closest('[data-product-id]').querySelector('.product-card__name, .pdp-title')?.textContent?.trim() || 'Product'
      : 'Product', 'Added to cart');
    CartDrawer.open();
    document.dispatchEvent(new CustomEvent('s3:cart-updated'));
  } catch (err) {
    S3Toast('Could not add to cart', err.message, true);
    btn.disabled = false;
    btn.innerHTML = origText;
  }
}

// Intercept all product form submits
on(document, 'submit', async e => {
  if (!e.target.matches('form[action="/cart/add"]')) return;
  e.preventDefault();
  const btn = e.target.querySelector('[type="submit"], [name="add"]');
  await S3AddToCart(e.target, btn);
  if (btn) { btn.disabled = false; btn.innerHTML = btn.dataset.origText || btn.innerHTML; }
});

// Store original ATC button text
document.addEventListener('DOMContentLoaded', () => {
  $$('[name="add"]').forEach(btn => { btn.dataset.origText = btn.innerHTML; });
});

/* ── Fly-to-cart animation ─────────────────────────────────── */
function flyToCart(srcEl) {
  const cartBtn = $('[data-open-cart]');
  if (!cartBtn || !srcEl) return;
  const s = srcEl.getBoundingClientRect();
  const t = cartBtn.getBoundingClientRect();
  const dot = document.createElement('div');
  dot.style.cssText = `
    position:fixed;left:${s.left+s.width/2-20}px;top:${s.top+s.height/2-20}px;
    width:40px;height:40px;border-radius:50%;background:var(--clr-red);
    display:flex;align-items:center;justify-content:center;
    pointer-events:none;z-index:9999;`;
  dot.innerHTML = `<svg width="16" height="16" fill="none" stroke="white" stroke-width="2.5" viewBox="0 0 24 24">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>`;
  document.body.appendChild(dot);
  dot.animate([
    { transform: 'translate(0,0) scale(1)', opacity: 1 },
    { transform: `translate(${t.left-s.left+t.width/2-20}px,${t.top-s.top+t.height/2-20}px) scale(.25)`, opacity: 0 }
  ], { duration: 650, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' })
    .onfinish = () => dot.remove();
}

/* ── Toast ─────────────────────────────────────────────────── */
function S3Toast(title, sub, isError = false) {
  let wrap = $('.s3-toast-wrap');
  if (!wrap) {
    wrap = Object.assign(document.createElement('div'), { className: 's3-toast-wrap' });
    wrap.style.cssText = 'position:fixed;bottom:1.5rem;right:1.5rem;z-index:9998;display:flex;flex-direction:column;gap:.75rem;pointer-events:none;';
    document.body.appendChild(wrap);
  }
  const t = document.createElement('div');
  t.style.cssText = `display:flex;align-items:center;gap:.75rem;
    background:var(--clr-surface-2);border:1px solid var(--clr-border);
    padding:.75rem 1.25rem;min-width:260px;max-width:340px;
    box-shadow:var(--sh-lg);pointer-events:all;
    animation:s3ToastIn .35s var(--ease-out) both;`;
  const color = isError ? 'var(--clr-error)' : 'var(--clr-success)';
  t.innerHTML = `<svg style="width:1.25rem;height:1.25rem;flex-shrink:0" fill="none" stroke="${color}" stroke-width="2.5" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="${isError
      ? 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z'
      : 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'}"/>
  </svg>
  <div><strong style="display:block;color:var(--clr-text);font-size:.8125rem;font-weight:700">${title}</strong>
    ${sub ? `<span style="font-size:.75rem;color:var(--clr-muted)">${sub}</span>` : ''}</div>`;
  wrap.prepend(t);
  setTimeout(() => {
    t.style.animation = 's3ToastOut .25s var(--ease-sharp) both';
    setTimeout(() => t.remove(), 260);
  }, 3400);
}

// Inject toast keyframes once
{
  const s = document.createElement('style');
  s.textContent = `
    @keyframes s3ToastIn  { from{opacity:0;transform:translateX(16px)} to{opacity:1;transform:translateX(0)} }
    @keyframes s3ToastOut { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(16px)} }
  `;
  document.head.appendChild(s);
}

/* ── Navigation ────────────────────────────────────────────── */
const S3Nav = {
  header: null, hamburger: null, mobileNav: null, ticking: false,

  open() {
    this.mobileNav?.classList.add('is-open');
    this.hamburger?.classList.add('is-open');
    this.hamburger?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  },
  close() {
    this.mobileNav?.classList.remove('is-open');
    this.hamburger?.classList.remove('is-open');
    this.hamburger?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  },
  toggle() { this.mobileNav?.classList.contains('is-open') ? this.close() : this.open(); },

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.header?.classList.toggle('is-scrolled', window.scrollY > 60);
        this.ticking = false;
      });
      this.ticking = true;
    }
  },

  init() {
    this.header    = $('#site-header');
    this.hamburger = $('[data-hamburger]');
    this.mobileNav = $('#mobile-nav');

    on(this.hamburger, 'click', () => this.toggle());
    on($('.mobile-nav__overlay'), 'click', () => this.close());
    on($('[data-close-mobile]'), 'click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });
    on(window, 'scroll', () => this.onScroll(), { passive: true });

    // Mark active nav links
    const path = window.location.pathname;
    $$('a[href]').forEach(a => {
      const href = a.getAttribute('href').split('?')[0];
      if (href && href !== '/' && path.startsWith(href)) {
        a.classList.add('is-current');
        a.setAttribute('aria-current', 'page');
      }
    });
  }
};

/* ── Quick View ─────────────────────────────────────────────── */
function S3QuickView(productId, card) {
  const overlay = $('#qv-overlay');
  const modal   = $('#qv-modal');
  if (!overlay || !modal) return;

  // Populate from card data
  const name  = card.querySelector('.product-card__name a')?.textContent || '';
  const price = card.querySelector('.product-card__price')?.textContent || '';
  const cat   = card.querySelector('.product-card__cat')?.textContent || '';
  const desc  = card.dataset.desc || '';
  const href  = card.querySelector('.product-card__name a')?.href || '#';

  modal.querySelector('#qv-name').textContent  = name;
  modal.querySelector('#qv-price').textContent = price;
  modal.querySelector('#qv-cat').textContent   = cat;
  modal.querySelector('#qv-desc').textContent  = desc;
  modal.querySelector('#qv-link').href         = href;

  overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  $('[class$="__close"], .qv-close', modal)?.focus();

  function close() { overlay.classList.remove('is-open'); document.body.style.overflow = ''; }
  on(overlay, 'click', e => { if (e.target === overlay) close(); }, { once: true });
  on($('.qv-close', modal), 'click', close, { once: true });
}

/* ── PDP Gallery Zoom ──────────────────────────────────────── */
const S3Gallery = {
  init() {
    const mainImg = $('.pdp-gallery__main-img');
    const wrap    = mainImg?.closest('.pdp-gallery__main');
    if (!mainImg || !wrap) return;

    mainImg.style.transition = 'transform .35s var(--ease-out), opacity .18s ease';

    on(wrap, 'mousemove', e => {
      const r = wrap.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width  * 100).toFixed(1);
      const y = ((e.clientY - r.top)  / r.height * 100).toFixed(1);
      mainImg.style.transformOrigin = `${x}% ${y}%`;
    });
    on(wrap, 'mouseenter', () => { mainImg.style.transform = 'scale(1.75)'; wrap.style.cursor = 'zoom-in'; });
    on(wrap, 'mouseleave', () => { mainImg.style.transform = 'scale(1)'; mainImg.style.transformOrigin = 'center'; });
  }
};

/* ── Sticky ATC (PDP) ──────────────────────────────────────── */
const S3StickyATC = {
  init() {
    const bar  = $('.sticky-atc');
    const trig = $('.pdp-main-atc');
    if (!bar || !trig || !window.IntersectionObserver) return;
    new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting), { threshold: 0.1 })
      .observe(trig);
  }
};

/* ── Accordions ─────────────────────────────────────────────── */
function S3Accordions() {
  $$('.accordion__trigger').forEach(btn => {
    btn.setAttribute('aria-expanded', 'false');
    on(btn, 'click', () => {
      const item   = btn.closest('.accordion__item');
      const isOpen = item.classList.contains('is-open');
      btn.closest('.accordion')?.querySelectorAll('.accordion__item.is-open').forEach(i => {
        if (i !== item) { i.classList.remove('is-open'); i.querySelector('.accordion__trigger')?.setAttribute('aria-expanded','false'); }
      });
      item.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
  });
}

/* ── Tabs ───────────────────────────────────────────────────── */
function S3Tabs() {
  $$('.tabs').forEach(wrap => {
    const btns   = $$('.tabs__btn', wrap);
    const panels = $$('.tabs__panel', wrap);
    btns.forEach(btn => {
      on(btn, 'click', () => {
        btns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected','false'); });
        panels.forEach(p => p.classList.remove('is-active'));
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        const t = btn.dataset.tab;
        if (t) wrap.querySelector('#' + t)?.classList.add('is-active');
      });
    });
    btns[0]?.click();
  });
}

/* ── Scroll Reveal ──────────────────────────────────────────── */
function S3Reveal() {
  if (!window.IntersectionObserver) return;
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.style.opacity = '1';
        e.target.style.transform = 'translateY(0)';
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  $$('[data-reveal]').forEach((el, i) => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(28px)';
    el.style.transition = 'opacity .65s var(--ease-out),transform .65s var(--ease-out)';
    el.style.transitionDelay = (el.dataset.revealDelay || (i % 4) * 80) + 'ms';
    obs.observe(el);
  });
}

/* ── Cart page helpers (used in main-cart.liquid) ───────────── */
function S3CartUpdateItem(key, qty) {
  ShopifyCart.update(key, qty).then(() => {
    document.dispatchEvent(new CustomEvent('s3:cart-updated'));
    location.reload();
  });
}

/* ── Init ───────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  S3Nav.init();
  S3Gallery.init();
  S3StickyATC.init();
  S3Accordions();
  S3Tabs();
  S3Reveal();
});
